/**
 * Analytics Dashboard Component
 *
 * Main dashboard component for displaying analytics data
 */

import React, { useState } from "react";
import { useAnalyticsData } from "../../hooks/useAnalyticsData";
import { AnalyticsFilters } from "../../types/analytics";
import { analyticsReportService } from "../../services/analytics/analyticsReportService";
import "./AnalyticsDashboard.scss";

interface AnalyticsDashboardProps {
	initialFilters?: AnalyticsFilters;
	className?: string;
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ initialFilters, className = "" }) => {
	const { data, loading, error, refresh, setFilters, filters } = useAnalyticsData(initialFilters);
	const [exportLoading, setExportLoading] = useState(false);

	const handleExportReport = async () => {
		if (exportLoading) return;

		try {
			setExportLoading(true);

			// Create filters based on current dashboard state
			const exportFilters: AnalyticsFilters = {
				dateRange: filters.dateRange,
				groupId: filters.groupId,
			};

			await analyticsReportService.exportMarkdownFile(exportFilters);
		} catch (error) {
			console.error("Failed to export analytics report:", error);
			alert("Failed to export report. Please try again.");
		} finally {
			setExportLoading(false);
		}
	};

	if (loading) {
		return (
			<div className={`analytics-dashboard loading ${className}`}>
				<div className="loading-spinner">
					<div className="spinner"></div>
					<p>Loading analytics data...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={`analytics-dashboard error ${className}`}>
				<div className="error-message">
					<h3>Failed to Load Analytics Data</h3>
					<p>{error}</p>
					<button onClick={refresh} className="retry-button">
						Retry
					</button>
				</div>
			</div>
		);
	}

	if (!data) {
		return (
			<div className={`analytics-dashboard no-data ${className}`}>
				<div className="no-data-message">
					<h3>No Analytics Data Available</h3>
					<p>There is no analytics data for the selected time period.</p>
				</div>
			</div>
		);
	}

	return (
		<div className={`analytics-dashboard ${className}`}>
			<div className="dashboard-header">
				<h1>Analytics Dashboard</h1>
				<div className="dashboard-controls">
					{/* <button onClick={refresh} className="refresh-button" disabled={loading}>
						Refresh
					</button> */}
					<button
						onClick={handleExportReport}
						className="export-button"
						disabled={loading || exportLoading}
						title="Export analytics report as markdown file">
						{exportLoading ? "Exporting..." : "Export Report"}
					</button>
				</div>
			</div>
		</div>
	);
};
